import { ColumnDef } from '@tanstack/react-table'

import { Buttons } from '@/components/common/buttons'
import { Table } from '@/components/common/data-display/Table'
import { Can } from '@/features/sessao/providers/auth-context'
import { useAuthStore } from '@/features/sessao/store/useAuthStore'
import { CallProduct } from '@/services/backend/config/entities'
import { NUMBER_TO_CURRENCY } from '@/utils/helpers/Currency'

import { ChamadoProdutoService } from '../../services/chamado-produto.service'

type Props = {
  data: {
    callProducts: CallProduct[]
    isEditable: boolean
  }
  onChange: (res: CallProduct) => void
}

export default function Component({
  data: { callProducts, isEditable },
  onChange,
}: Props) {
  const { user } = useAuthStore()

  const onChangeQuantity = async (action: 'add' | 'remove', id: string) => {
    try {
      const res = await ChamadoProdutoService[action]({ id })
      onChange(res)
    } catch {
      // Error toast is already shown by the API connector interceptor
    }
  }

  const columns: ColumnDef<(typeof callProducts)[0]>[] = [
    {
      header: 'Produto',
      accessorKey: 'product',
      sortingFn: (a, b) =>
        (a.original.productName ?? '') > (b.original.productName ?? '')
          ? 1
          : -1,
      cell: ({ row }) => {
        return (
          <div>
            <div className="text-xs font-bold">{row.original.productSku}</div>
            <div className="text-sm">{row.original.productName}</div>
          </div>
        )
      },
    },
    {
      header: 'Qtd.',
      accessorKey: 'quantity',
    },
  ]

  if (user) {
    const hiddenColumns: ColumnDef<(typeof callProducts)[0]>[] = [
      {
        header: 'R$ Unit.',
        accessorKey: 'unitCost',
        cell: ({ row }) => (
          <div className="text-right">
            {NUMBER_TO_CURRENCY(row.original.unitCost ?? 0)}
          </div>
        ),
      },
      {
        header: 'R$ Total',
        accessorKey: 'totalCost',
        cell: ({ row }) => {
          const total = (row.original.unitCost ?? 0) * row.original.quantity
          return <div className="text-right">{NUMBER_TO_CURRENCY(total)}</div>
        },
      },
      {
        header: '',
        accessorKey: 'actions',
        enableSorting: false,
        cell: ({ row }) =>
          isEditable ? (
            <Can asDefault I="update" a="CallItem">
              <div className="flex justify-end gap-2">
                <Buttons.Add
                  onClick={() => onChangeQuantity('add', row.original.id)}
                />
                <Buttons.Remove
                  onClick={() => onChangeQuantity('remove', row.original.id)}
                />
              </div>
            </Can>
          ) : null,
      },
    ]

    columns.push(...hiddenColumns)
  }

  return (
    <Table
      data={callProducts}
      columns={columns}
      initialSorting={[{ id: 'product', desc: false }]}
    />
  )
}
